import { NetworkStatus } from '../../services/NetworkMonitoringService';

export function shouldHandleNetworkStatusChange({
  isTutor,
  isListening,
  newStatus,
  isTapToRead,
  isLocalAsr,
}) {
  if (!isTutor) {
    return false;
  }

  // Local ASR decodes on-device, so the student can keep reading through a bad
  // connection and the cursor keeps advancing. Auto-progressing the cursor and
  // pausing the session would interrupt a session that is still working, so the
  // UX response is suppressed. Monitoring itself is untouched — NetworkMonitoringService
  // keeps measuring and activity_network_health still reports badNetworkCount.
  // Callers pass whether local ASR is actually driving placement (not merely enabled),
  // so a session that fell back to the server keeps the full bad-network response.
  // Like tap-to-read, skipping entry means no interval or pause is ever created, so
  // the Normal-status recovery branch has nothing to clean up.
  if (isLocalAsr) {
    return false;
  }

  // Tap-to-read advances the cursor only by tap (TT-1004 AC: "Cursor never advances
  // automatically via ASR while in this mode"). The bad-network flow auto-progresses the
  // cursor via skipWord() on a timer, so it must not run in tap mode. Skipping entry here
  // also means no interval/pause is ever created, so the Normal-status recovery branch has
  // nothing to clean up — returning false for every transition in tap mode is safe.
  if (isTapToRead) {
    return false;
  }

  if (newStatus === NetworkStatus.Normal) {
    return true;
  }

  return Boolean(isListening);
}

export function shouldRunNetworkRecoveryActions({
  isListening,
  isAdministrative,
}) {
  // Recovery actions (resuming auto-progress, dismissing the pause modal)
  // restart listening, so they are only valid for an evidence-based recovery
  // while the mic is live. An administrative Normal — monitoring turned off
  // for an intervention, listening stop, or phrase end — must only clear
  // timers, never take over the mic from whatever flow deactivated monitoring.
  if (isAdministrative) {
    return false;
  }
  return Boolean(isListening);
}

export const PausedNormalAction = Object.freeze({
  Resume: 'resume', // evidence-based recovery while listening — run the full resume-from-pause flow
  Clear: 'clear',   // any other Normal while paused — reset the state and close the stuck pause modal
  None: 'none',     // not paused — nothing to do on this transition
});

/**
 * Decides what a Normal transition should do to the bad-network pause modal.
 * Split on isAdministrative, NOT isListening: while paused the mic is off because
 * WE turned it off for the pause, so "not listening" here does not mean another
 * flow owns the mic.
 *  - Administrative Normal (monitoring turned off for an intervention/stop): that
 *    flow already owns the mic and will resume reading, so just Clear the stuck
 *    modal (resetting pausedFromBadNetwork so the componentDidUpdate guard can
 *    re-arm monitoring next phrase).
 *  - Evidence-based Normal (queue drained), including the brief not-listening
 *    window inside pauseFromBadNetwork before its delayed startListening: a
 *    genuine recovery we own — run the full Resume flow (resumeFromBadNetworkPause
 *    restores the phrase timers and the mic). A bare Clear here would leave the
 *    reader dead: modal gone, timers cleared, mic off.
 */
export function resolvePausedNormalAction({
  pausedFromBadNetwork,
  isAdministrative,
}) {
  if (!pausedFromBadNetwork) {
    return PausedNormalAction.None;
  }
  return isAdministrative ? PausedNormalAction.Clear : PausedNormalAction.Resume;
}
